import { Component, Input } from '@angular/core';
import { SolIcon } from '@sol/icons';
import { AbstractDialogComponent } from '../abstract-dialog-component.directive';
import { SolButton } from '../../../models/core/sol-button';
import { SolImage } from '../../../models/core/sol-image';
import { SolButtonComponent } from '../../core/sol-button/sol-button.component';
import { SolIconComponent } from '../../core/sol-icon/sol-icon.component';
import { SolLoaderComponent } from '../../core/sol-loader/sol-loader.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolDialogComponent } from '../sol-dialog/sol-dialog.component';
import { SolImageDialogState } from './sol-image-dialog.state';

/**
 * Lightbox that shows one image as large as the viewport allows: the full-resolution source when the image carries
 * one, the image's own source otherwise, and an error state when neither loads. The close button, Escape and the focus
 * trap come from the chromeless `sol-dialog`; a click on the dimmed surround closes it too. Every one of those routes
 * runs through `close()`, so `onClosed` lets an owner release what it allocated for the image.
 *
 * The error state is this component's own rather than `<sol-empty-state>`, for two reasons: that component reaches
 * `sol-text-box` → `sol-markdown` → `sol-content` → `sol-image`, which imports this viewer back, and its ink colours
 * are built for a light surface where a lightbox is a dark one.
 */
@Component({
  selector: 'sol-image-dialog',
  imports: [SolButtonComponent, SolDialogComponent, SolIconComponent, SolLoaderComponent, TranslatePipe],
  templateUrl: './sol-image-dialog.component.html',
  host: { '[attr.title]': 'null' },
})
export class SolImageDialogComponent extends AbstractDialogComponent<SolImageDialogState> {
  // Static refs
  protected readonly SolIcon = SolIcon;

  // Computed
  protected readonly dialog$ = this.compute(state => state.dialog);
  protected readonly title$ = this.compute(state => state.title);
  protected readonly hasToolbar$ = this.compute(state => state.hasToolbar);
  protected readonly alt$ = this.compute(state => state.image.alt);
  protected readonly actionButton$ = this.compute(state => state.actionButton);
  protected readonly loading$ = this.compute(state => state.loading);
  protected readonly hasError$ = this.compute(state => state.hasError);
  protected readonly displaySrc$ = this.compute(state => state.displaySrc);
  protected readonly loader$ = this.compute(state => state.loader);

  public onActionClick: () => void = () => {};
  public onClosed: () => void = () => {};

  constructor() {
    super(SolImageDialogState.of());
  }

  @Input()
  public set image(image: SolImage) {
    this.update(state => state.withImage(image));
  }

  @Input()
  public set title(title: string) {
    this.update(state => state.withTitle(title));
  }

  @Input()
  public set actionButton(actionButton: SolButton | undefined) {
    this.update(state => state.withActionButton(actionButton));
  }

  @Input()
  public set loading(loading: boolean) {
    this.update(state => state.withLoading(loading));
  }

  @Input()
  public set error(error: boolean) {
    this.update(state => state.withError(error));
  }

  public override close(): void {
    super.close();
    this.onClosed();
  }

  public showErrors(): void {
    // The viewer has no form — failures show as the error empty state.
  }

  protected onSurroundClick(): void {
    this.close();
  }

  protected onLoadError(): void {
    this.update(state => state.withLoadFailure());
  }

  protected handleConfirm(): void {
    // The viewer has no confirm button — close button only.
  }
}
